import { clsx } from "clsx";
import type { ReactNode } from "react";
import { IconPill, type IconPillTone } from "../icon-pill";
import { Heading, Text } from "../typography";

export interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: string | number;
  /** Optional secondary line under the headline value (e.g. a sum amount). */
  sub?: string;
  iconTone?: IconPillTone;
  className?: string;
  /**
   * When provided, the card renders as a button: it gets a cursor-pointer +
   * a "lift" hover (translate + shadow + slight icon scale), and clicking
   * fires onClick. Mirrors the landing-page category card affordance.
   */
  onClick?: () => void;
}

export function StatCard({ icon, label, value, sub, iconTone = "cyanTint", className, onClick }: StatCardProps) {
  const interactive = !!onClick;

  const baseClasses = clsx(
    "flex w-full items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-4 text-left",
    "shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
    interactive &&
      "cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:-translate-y-0.5 focus-visible:shadow-[0_6px_18px_rgba(16,24,40,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan/40",
    className,
  );

  const inner = (
    <>
      <IconPill
        size={48}
        tone={iconTone}
        className={interactive ? "transition-transform duration-200 ease-out group-hover:scale-105 group-focus-visible:scale-105" : undefined}
      >
        {icon}
      </IconPill>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <Text variant="bodySm" tone="muted" className="truncate">
          {label}
        </Text>
        <Heading variant="h3" tone="dark">
          {value}
        </Heading>
        {sub ? (
          <Text variant="bodyXs" tone="muted" className="truncate">
            {sub}
          </Text>
        ) : null}
      </div>
    </>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={clsx("group", baseClasses)}>
        {inner}
      </button>
    );
  }

  return <div className={baseClasses}>{inner}</div>;
}

export default StatCard;
